import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorState, LoadingState } from '@/components/feedback/states';
import { useSessionState } from '@/features/session/use-session';
import { ERROR_CODE } from '@obraplan/core/application/errors/error-codes';
import { AppShell } from './layouts/AppShell';
import { HomePage } from '@/pages/HomePage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { SchedulePage } from '@/pages/SchedulePage';
import { ExecutionPage } from '@/pages/ExecutionPage';
import { ScheduleItemsPage } from '@/pages/ScheduleItemsPage';
import { LoginPage } from '@/pages/LoginPage';

import { StructurePage } from '@/pages/StructurePage';
import { TakeoffPage } from '@/pages/TakeoffPage';
import { WeeklyPlanPage } from '@/pages/WeeklyPlanPage';
import { DailyPlanPage } from '@/pages/DailyPlanPage';
import { InspectionPage } from '@/pages/InspectionPage';
import { QualitySheetPage } from '@/pages/QualitySheetPage';
import { NonConformitiesPage } from '@/pages/NonConformitiesPage';
import { WorkforcePage } from '@/pages/WorkforcePage';
import { SuppliesPage } from '@/pages/SuppliesPage';
import { AuditPage } from '@/pages/settings/AuditPage';
import { CalendarPage } from '@/pages/settings/CalendarPage';
import { DisciplinesPage } from '@/pages/settings/DisciplinesPage';
import { ServicesPage } from '@/pages/settings/ServicesPage';
import { ChecklistsPage } from '@/pages/settings/ChecklistsPage';
import { IntegrityPage } from '@/pages/settings/IntegrityPage';
import { MaterialsPage } from '@/pages/settings/MaterialsPage';
import { MyProfilePage } from '@/pages/MyProfilePage';
import { ProfilesPage } from '@/pages/settings/ProfilesPage';
import { StoragePage } from '@/pages/settings/StoragePage';

/**
 * Roteamento.
 *
 * Sem sessão, as únicas telas alcançáveis são a de entrada e a que consome o link
 * (RN-130). Isso é conveniência de navegação, não autorização: cada caso de uso verifica
 * permissão por conta própria (RN-134).
 */
export function AppRouter() {
  const { status, errorMessage } = useSessionState();

  if (status === 'initializing') {
    return <LoadingState label="Abrindo o sistema…" />;
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <ErrorState
          message={errorMessage ?? 'Não foi possível abrir o banco de dados local.'}
          code="STORAGE.UNAVAILABLE"
          onRetry={() => globalThis.location.reload()}
        />
      </div>
    );
  }

  if (status === 'offline') {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <ErrorState
          message={errorMessage ?? 'Sem conexão com o servidor.'}
          code={ERROR_CODE.NO_CONNECTION}
          onRetry={() => globalThis.location.reload()}
        />
      </div>
    );
  }

  if (status === 'no-session') {
    return (
      <Routes>
        <Route path="/entrar" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/entrar" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/entrar" element={<Navigate to="/" replace />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/obras" element={<ProjectsPage />} />
        <Route path="/estrutura" element={<StructurePage />} />
        {/* Três rotas, uma tela: a aba sai do caminho, e é a própria `TakeoffPage` quem
            lê qual deles está aberto. Passar a aba por prop não funcionava — as três
            renderizam o mesmo componente na mesma posição, então ele não remonta. */}
        <Route path="/quantitativo" element={<TakeoffPage />} />
        <Route path="/quantitativo/itens" element={<TakeoffPage />} />
        <Route path="/quantitativo/orcamento" element={<TakeoffPage />} />
        <Route path="/cronograma" element={<SchedulePage />} />
        {/* O cronograma por item é a segunda metade da tela Cronograma, e não uma tela com
            permissão própria: `pageForPath` já a trata como subcaminho de `/cronograma`. */}
        <Route path="/cronograma/itens" element={<ScheduleItemsPage />} />
        {/* A execução por cômodo (Fase 34, D14): outra metade do Cronograma, com a permissão dele. */}
        <Route path="/cronograma/execucao" element={<ExecutionPage />} />
        <Route path="/mao-de-obra" element={<WorkforcePage />} />
        <Route path="/planejamento" element={<WeeklyPlanPage />} />
        <Route path="/programacao" element={<DailyPlanPage />} />
        <Route path="/conferencia" element={<InspectionPage />} />
        <Route path="/qualidade" element={<QualitySheetPage />} />
        <Route path="/nao-conformidades" element={<NonConformitiesPage />} />
        <Route path="/suprimentos" element={<SuppliesPage />} />
        <Route path="/meu-perfil" element={<MyProfilePage />} />
        <Route path="/configuracoes/disciplinas" element={<DisciplinesPage />} />
        <Route path="/configuracoes/servicos" element={<ServicesPage />} />
        <Route path="/configuracoes/checklists" element={<ChecklistsPage />} />
        <Route path="/configuracoes/materiais" element={<MaterialsPage />} />
        <Route path="/configuracoes/perfis" element={<ProfilesPage />} />
        <Route path="/configuracoes/calendario" element={<CalendarPage />} />
        <Route path="/configuracoes/armazenamento" element={<StoragePage />} />
        <Route path="/configuracoes/integridade" element={<IntegrityPage />} />
        <Route path="/configuracoes/auditoria" element={<AuditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
